import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserDropdown from '../UserDropdown';
import ReportForm from '../Shared/ReportForm';
import MyReports from '../Shared/MyReports';

const DriverHelpSupport = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showMyReports, setShowMyReports] = useState(false);
  const [reportKey, setReportKey] = useState(0);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const faqSections = [
    {
      title: 'Getting Started',
      items: [
        {
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password" on the login page and enter your email address. You\'ll receive an email with a link to reset your password. The link expires after 4 hours for security purposes.'
        }
      ]
    },
    {
      title: 'Availability & Pricing',
      items: [
        {
          question: 'How do I set my availability?',
          answer: 'Go to Settings > Availability to configure your weekly schedule. You can set up to 3 time windows per day of the week (for example, morning, afternoon, and evening shifts). You can also set blackout periods for vacations or days you know you won\'t be available.'
        },
        {
          question: 'How quickly do availability changes take effect?',
          answer: 'Availability changes take effect immediately. As soon as you save your changes, new ride requests will see your updated availability. Note that existing scheduled rides are not affected by availability changes.'
        },
        {
          question: 'How do I set my pricing?',
          answer: 'Go to Settings > Pricing Rules to configure your rates. You can set different base fares and per-mile rates for different days of the week and times of day. This allows you to charge premium rates during high-demand periods.'
        },
        {
          question: 'How quickly do pricing changes take effect?',
          answer: 'Pricing changes take effect immediately for new ride requests. However, any existing quotes or scheduled rides will keep their original quoted price - this ensures riders aren\'t surprised by price changes after they\'ve booked.'
        },
        {
          question: 'How do I add a blackout period?',
          answer: 'Blackout periods can be added by navigating to Settings > Availability, and scrolling to the bottom to add partial or full-day blackouts as needed to block off your schedule for any reason.'
        }
      ]
    },
    {
      title: 'Day Bookings',
      items: [
        {
          question: 'What are day bookings?',
          answer: 'Day bookings are extended reservations where a rider books you for a half-day (up to 6 hours) or full-day (up to 12 hours) at a flat rate. These are ideal for events, airport trips with multiple stops, business meetings, or when riders need a dedicated driver for an extended period.'
        },
        {
          question: 'How do I set up day booking rates?',
          answer: 'Go to Settings > Day Rates to configure your pricing tiers. You\'ll set flat rates for different mileage ranges (for example: $150 for 0-50 miles, $250 for 51-100 miles, $400 for 101+ miles). You\'ll configure separate rates for half-day and full-day bookings.'
        },
        {
          question: 'How is day booking pricing calculated?',
          answer: 'When a rider creates a day booking with multiple stops, the system calculates the total mileage across all legs of the trip. This total mileage determines which of your mileage tiers applies, and the rider pays the flat rate for that tier.'
        }
      ]
    },
    {
      title: 'Driver Tiers & Commissions',
      items: [
        {
          question: 'What are the driver tiers?',
          answer: 'RideHowl has 5 subscription tiers: Captain, Navigator, Cruiser, Commuter, and Starter (free). Higher tiers have lower commission rates, meaning you keep more of each fare. For example, Captain tier drivers keep 97% of their fares, while Starter tier drivers keep 70%.'
        },
        {
          question: 'What is the commission cap?',
          answer: 'Platform commission is capped at $30 per ride, regardless of your tier. This means on high-value rides, you benefit even more - for example, on a $200 ride, even a Starter tier driver would only pay $30 in commission (not $60), keeping $170.'
        },
        {
          question: 'What features do premium tiers include?',
          answer: 'Beyond lower commission rates, premium tiers unlock additional features:\n\n- All premium tiers (Captain through Commuter): Real-time travel time calculations between rides to help you optimize your schedule and see how far your next pickup is from your current location.\n\n- Navigator tier and above: Advanced payout analytics including tips breakdown, earnings per mile, earnings per trip, and other performance metrics to help you maximize your earnings.\n\n- Future Updates: We are constantly working on improvements and new features. As they are added to the application, typically our premium tiers (Navigator and Captain) will experience these new features first, and if they are successful and efficient to deploy across the platform we will either deploy to all users or paying users depending on the cost-effeciency of the new feature (i.e. if it requires costly API calls or similar, it will stay within the paying tiers, but if it does not have any variable costs associated, it will likely be deployed to all driver tiers).'
        }
      ]
    },
    {
      title: 'Sharing & Marketing',
      items: [
        {
          question: 'How do I invite riders to book with me?',
          answer: 'Click "Invite Riders to Book" on your dashboard. You can: (1) Scan or download the QR code to print on business cards or flyers, (2) Copy the link to share anywhere, or (3) Send the link directly via email or SMS to potential customers.'
        },
        {
          question: 'Can customers book without creating an account first?',
          answer: 'When customers use your booking link, they\'ll be prompted to create an account or log in before completing their booking. This ensures you have their contact information and payment details for the ride. The account creation process for riders takes less than 2 minutes!'
        }
      ]
    },
    {
      title: 'Payments & Payouts',
      items: [
        {
          question: 'When do I get paid?',
          answer: 'Payouts are triggered daily for our drivers. Payouts are processed through Stripe and batched together to minimize fees for our drivers (saves you $.30 per tip!). Your ride fares and any tips from riders are combined into a single payout, which is typically available in your bank account within 2-3 business days after a ride is completed (this includes the 24 hour tip window after a ride is performed, to ensure the customer has adequate time to tip). You can view your payout history and pending amounts in the Payouts section of your dashboard.'
        },
        {
          question: 'How are tips handled?',
          answer: 'Tips are batched together with your ride fares into a single payout to save you money. By combining tips with fares instead of processing them separately, we save you approximately $0.30 per tip received (the fixed portion of payment processing fees). Tips still go directly to you with only the standard percentage-based processing fee (2.9%) applied, and are broken out into Tip and Fare lines for reporting purposes in the Stripe payout process. If you are a Navigator Tier or higher, your payout summary shows a breakdown of fares and tips received, as well as key metrics such as Tips / Ride and Fares / Mile to help you manage your profitability.'
        }
      ]
    },
    {
      title: 'Managing Rides',
      items: [
        {
          question: 'How do I accept or decline ride requests?',
          answer: 'When you receive a ride request, you\'ll see it on your dashboard with all the details (pickup/dropoff locations, time, and quoted price). Click "Accept" to confirm the ride or "Decline" if you\'re unable to fulfill it. Responding promptly helps maintain a good experience for riders.'
        },
        {
          question: 'How does the counter-offer process work?',
          answer: 'When a rider requests a ride, they receive an initial quote based on your pricing rules. If you feel the quote doesn\'t reflect the true value of the trip (due to traffic, special circumstances, or other factors), you can submit a counter-offer with an adjusted price.\n\nHere\'s how it works:\n1. Rider requests a ride and receives an initial quote\n2. You review the request and can either accept the quoted price or submit a counter-offer\n3. If you counter-offer, the rider is notified and can accept or reject your new price\n4. If the rider accepts your counter-offer, you\'ll receive a final confirmation request\n5. Once you give final approval, the ride is officially booked and the rider\'s payment is initiated\n\nThis process ensures both parties agree on the final price before the ride is confirmed.'
        },
        {
          question: 'How do I cancel a ride?',
          answer: 'If you need to cancel an accepted ride, go to the ride details and select "Cancel Ride". Please only cancel when absolutely necessary, as cancellations affect rider experience. If you need to cancel frequently, consider adjusting your availability settings instead.'
        },
        {
          question: 'How do I mark a ride as complete?',
          answer: 'Once you\'ve completed a ride, click "Complete Ride" from the ride details. This triggers the payment process and allows the rider to add a tip. Make sure to complete rides promptly to ensure you receive payment in a timely manner.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/driver/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Help & Support</h1>
            </div>
            <UserDropdown userType="driver" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Support</h2>
          <p className="text-gray-600 mb-4">
            Need help? Our support team is here for you.
          </p>
          <a
            href="mailto:support@ridehowl.com"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium mb-6"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            support@ridehowl.com
          </a>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-3">
              This email can be used for Technical Support, Product Improvement Requests, and New Feature Requests.
            </p>
            <p className="text-sm text-blue-700">
              Please use the following subject lines:
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li><strong>"Support"</strong> - for any app difficulties, errors, or urgent support needed</li>
              <li><strong>"Improvement"</strong> - for requests to improve existing features</li>
              <li><strong>"New Feature Request"</strong> - to request new features in future releases</li>
            </ul>
          </div>
        </div>

        {/* Submit a Report */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Submit a Report</h2>
            <button
              onClick={() => setShowReportForm(!showReportForm)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              {showReportForm ? 'Hide Form' : 'New Report'}
            </button>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Report bugs, safety concerns, inappropriate behavior, payment disputes, or other issues.
          </p>
          {showReportForm && (
            <ReportForm
              userType="driver"
              onSuccess={() => {
                setReportKey(prev => prev + 1);
                setShowMyReports(true);
              }}
              onCancel={() => setShowReportForm(false)}
            />
          )}
        </div>

        {/* My Reports */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <button
            onClick={() => setShowMyReports(!showMyReports)}
            className="w-full flex items-center justify-between"
          >
            <h2 className="text-lg font-semibold text-gray-900">My Reports</h2>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${showMyReports ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          {showMyReports && (
            <div className="mt-4">
              <MyReports key={reportKey} />
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h2>
          </div>

          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="border-b border-gray-200 last:border-b-0">
              <button
                onClick={() => toggleSection(sectionIndex)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
              >
                <span className="font-medium text-gray-900">{section.title}</span>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSection === sectionIndex ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>

              {expandedSection === sectionIndex && (
                <div className="px-6 pb-4">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="mb-4 last:mb-0">
                      <h4 className="font-medium text-gray-800 mb-2">{item.question}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{item.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default DriverHelpSupport;
